/**
 * HelpContext — React Context for the AI Help Engine
 *
 * Key concepts:
 *   formName      — canonical identifier; same string used for:
 *                     - POST/GET to /api/help/:proj/:form/*
 *                     - localStorage registry keys
 *                     - Bootstrapper grouping
 *   data-help-form — DOM boundary attribute that HelpProvider renders on a
 *                    wrapper span. The bootstrapper and registry scanner
 *                    walk up to this attribute to decide which form a
 *                    [data-help-control] belongs to. Guarantees 1:1 match.
 *   iconsVisible   — (i) icons rendered only when true. Toggle via Ctrl+H.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE as API_URL } from '../lib/api';

const LS_VIS = 'retailcliq_help_icons';
const LS_HINT = 'retailcliq_help_hint_seen';
const REG_KEY = 'retailcliq_help_registry';

export const HelpContext = createContext({
  projectName: 'RetailERP',
  formName: '',
  canEdit: false,
  helpDict: {},
  isLoading: false,
  iconsVisible: false,
  toggleIcons: () => {},
  refreshDict: async () => {},
  setFormName: (_) => {},
});

export const useHelpContext = () => useContext(HelpContext);

export function HelpProvider({ children, projectName = 'RetailERP', formName: initialForm = '', userRole = '' }) {
  const [formName, setFormName] = useState(initialForm);
  const [helpDict, setHelpDict] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [iconsVisible, setIconsVisible] = useState(() => {
    try { return localStorage.getItem(LS_VIS) === '1'; } catch { return false; }
  });
  const [hintVisible, setHintVisible] = useState(false);
  const hintTimerRef = useRef(null);
  const scopeRef = useRef(null);

  const canEdit = (() => {
    let role = userRole || '';
    let features = [];
    if (!role) {
      // Fallback — read from legacy/portal storage so screens that don't explicitly
      // pass userRole still get edit rights for sys-admins.
      try {
        const raw = localStorage.getItem('retailcliq_user') || sessionStorage.getItem('retailcliq_user') || localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user');
        if (raw) {
          const u = JSON.parse(raw);
          role = u?.role || u?.user?.role || '';
        }
      } catch (_) { /* silent */ }
    }
    // Read adminFeatures from global context (stored in rcq_global_context or retailcliq_global_context)
    try {
      const raw = localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context') || localStorage.getItem('retailcliq_global_context') || sessionStorage.getItem('retailcliq_global_context');
      if (raw) {
        const ctx = JSON.parse(raw);
        features = Array.isArray(ctx?.adminFeatures) ? ctx.adminFeatures : [];
      }
    } catch (_) { /* silent */ }
    if (role === 'SUPER_ADMIN' || role === 'sys-admin' || role === 'superAdmin' || role === 'admin') return true;
    // ADMINSUPPORT/LICENSOR with DYNAMIC_ATTRIBUTES feature grant can edit help texts
    if ((role === 'ADMINSUPPORT' || role === 'LICENSOR') && features.includes('DYNAMIC_ATTRIBUTES')) return true;
    return false;
  })();

  const toggleIcons = useCallback(() => {
    setIconsVisible((v) => {
      const nv = !v;
      try { localStorage.setItem(LS_VIS, nv ? '1' : '0'); } catch {}
      return nv;
    });
  }, []);

  // Keep formName in sync when the wrapping component re-renders with a new form
  useEffect(() => { setFormName(initialForm); }, [initialForm]);

  // Ctrl+Shift+H global toggle (Shift avoids collision with browser History)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && (e.key === 'h' || e.key === 'H')) {
        const t = e.target;
        const tag = (t?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
        e.preventDefault();
        toggleIcons();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleIcons]);

  const fetchDict = useCallback(async (proj, form) => {
    if (!form) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/help/dictionary/${encodeURIComponent(proj)}/${encodeURIComponent(form)}`);
      if (res.ok) setHelpDict((await res.json()) || {});
    } catch { /* silent */ } finally { setIsLoading(false); }
  }, []);

  // Fetch dict + register form controls in the Known Forms Registry
  useEffect(() => {
    fetchDict(projectName, formName);
    if (!formName) return;
    const t = setTimeout(() => {
      try {
        const root = scopeRef.current;
        if (!root) return;
        const nodes = root.querySelectorAll('[data-help-control]');
        const seen = new Set();
        const controls = [];
        nodes.forEach((n) => {
          const id = n.dataset.helpControl;
          if (!id || seen.has(id)) return;
          // Only include controls that actually belong to THIS form boundary
          // (i.e. no nested HelpProvider interposes between them).
          const owner = n.closest('[data-help-form]');
          if (owner && owner !== root) return;
          seen.add(id);
          const label =
            n.getAttribute('aria-label') ||
            (n.previousElementSibling?.textContent || '').trim() ||
            n.closest('label')?.textContent?.trim() ||
            n.placeholder || id;
          controls.push({ controlId: id, controlName: (label || id).substring(0, 60) });
        });
        if (!controls.length) return;
        const reg = (() => { try { return JSON.parse(localStorage.getItem(REG_KEY) || '{}'); } catch { return {}; } })();
        reg[formName] = { controls, lastSeen: new Date().toISOString() };
        try { localStorage.setItem(REG_KEY, JSON.stringify(reg)); } catch {}
      } catch { /* silent */ }
    }, 500);
    return () => clearTimeout(t);
  }, [projectName, formName, fetchDict]);

  // One-time hint per session
  useEffect(() => {
    if (!formName) return;
    let seen = false;
    try { seen = sessionStorage.getItem(LS_HINT) === '1'; } catch {}
    if (seen) return;
    setHintVisible(true);
    try { sessionStorage.setItem(LS_HINT, '1'); } catch {}
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setHintVisible(false), 4500);
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, [formName]);

  const refreshDict = useCallback(() => fetchDict(projectName, formName), [projectName, formName, fetchDict]);

  return (
    <HelpContext.Provider value={{ projectName, formName, canEdit, helpDict, isLoading, iconsVisible, toggleIcons, refreshDict, setFormName }}>
      <span ref={scopeRef} data-help-form={formName || '__none__'} style={{ display: 'contents' }}>
        {children}
      </span>
      {hintVisible && (
        <div
          data-testid="help-hint-banner"
          style={{
            position: 'fixed',
            top: 64,
            right: 16,
            zIndex: 100000,
            background: 'linear-gradient(135deg, #1e293b, #334155)',
            color: '#f8fafc',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            border: '1px solid rgba(124,58,237,0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ background: '#7c3aed', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>TIP</span>
          Press <kbd style={kbdStyle}>Ctrl</kbd> + <kbd style={kbdStyle}>Shift</kbd> + <kbd style={kbdStyle}>H</kbd> to {iconsVisible ? 'hide' : 'show'} field help icons
          <button onClick={() => setHintVisible(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', marginLeft: 6, fontSize: 14, lineHeight: 1 }} aria-label="Dismiss hint">×</button>
        </div>
      )}
    </HelpContext.Provider>
  );
}

const kbdStyle = {
  background: '#0f172a',
  border: '1px solid #475569',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 11,
  fontFamily: "'SF Mono', monospace",
  color: '#e2e8f0',
};
